import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { supabase } from '@/lib/supabase'
import type { VehiclePricingAddon } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

import {
  vehicleAddonSchema,
  ADDON_PRICE_TYPES,
  ADDON_PRICE_TYPE_LABELS,
  COMMON_ADDONS,
  type VehicleAddonFormData,
} from '@/schemas/vehicle'

interface VehicleAddonDialogProps {
  addonId: string | null
  pricingId: string
  accountId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function VehicleAddonDialog({
  addonId,
  pricingId,
  accountId,
  open,
  onOpenChange,
}: VehicleAddonDialogProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const isEditing = !!addonId

  const { data: addon, isLoading: isLoadingAddon } = useQuery({
    queryKey: ['vehicle-addon', addonId],
    queryFn: async () => {
      if (!addonId) return null
      const { data, error } = await supabase
        .from('vehicle_pricing_addons')
        .select('*')
        .eq('id', addonId)
        .single()
      if (error) throw error
      return data as VehiclePricingAddon
    },
    enabled: !!addonId && open,
  })

  const form = useForm<VehicleAddonFormData>({
    resolver: zodResolver(vehicleAddonSchema),
    defaultValues: {
      name: '',
      description: '',
      price: 0,
      price_type: 'flat',
      max_quantity: 1,
      is_active: true,
    },
  })

  useEffect(() => {
    if (addon) {
      form.reset({
        name: addon.name,
        description: addon.description || '',
        price: Number(addon.price),
        price_type: addon.price_type as VehicleAddonFormData['price_type'],
        max_quantity: addon.max_quantity,
        is_active: addon.is_active,
      })
    } else if (!isEditing && open) {
      form.reset({
        name: '',
        description: '',
        price: 0,
        price_type: 'flat',
        max_quantity: 1,
        is_active: true,
      })
    }
  }, [addon, form, isEditing, open])

  const createMutation = useMutation({
    mutationFn: async (data: VehicleAddonFormData) => {
      const { error } = await supabase.from('vehicle_pricing_addons').insert({
        ...data,
        vehicle_pricing_id: pricingId,
        account_id: accountId,
        description: data.description || null,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      toast({ title: 'Add-on created successfully' })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (data: VehicleAddonFormData) => {
      if (!addonId) throw new Error('Add-on ID is required')
      const { error } = await supabase
        .from('vehicle_pricing_addons')
        .update({
          ...data,
          description: data.description || null,
        })
        .eq('id', addonId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['vehicle-addon', addonId] })
      toast({ title: 'Add-on updated successfully' })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const onSubmit = (data: VehicleAddonFormData) => {
    if (isEditing) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset()
    }
    onOpenChange(open)
  }

  const handleQuickAdd = (addon: (typeof COMMON_ADDONS)[number]) => {
    form.setValue('name', addon.name)
    form.setValue('price', addon.price)
    form.setValue('price_type', addon.price_type)
    form.setValue('max_quantity', addon.max_quantity)
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Add-on' : 'Add Add-on'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the add-on information.'
              : 'Add a new add-on to this pricing option.'}
          </DialogDescription>
        </DialogHeader>

        {isEditing && isLoadingAddon ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {!isEditing && (
                <div className="space-y-2">
                  <Label>Quick Add</Label>
                  <div className="flex flex-wrap gap-2">
                    {COMMON_ADDONS.slice(0, 4).map((a) => (
                      <Button
                        key={a.name}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickAdd(a)}
                      >
                        {a.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Add-on Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Child Car Seat" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe the add-on..."
                        className="min-h-[60px]"
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price</FormLabel>
                      <FormControl>
                        <CurrencyInput
                          placeholder="15.00"
                          value={field.value}
                          onChange={(value) => field.onChange(value ?? 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="price_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ADDON_PRICE_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {ADDON_PRICE_TYPE_LABELS[type]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="max_quantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max Quantity</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          max={99}
                          placeholder="1"
                          {...field}
                          value={field.value ?? 1}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? Number(e.target.value) : 1
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        1 = toggle, &gt;1 = dropdown
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Active</FormLabel>
                      <FormDescription>
                        This add-on will be available for selection
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isEditing ? 'Update Add-on' : 'Add Add-on'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
